import { motion } from "framer-motion";
import { 
  Target, Lightbulb, TrendingUp, CheckCircle, Zap
} from "lucide-react";
import { SEO } from "@/components/shared";
import { PHOTOS, PhotoBand, PhotoMosaic, CardImage } from "@/components/Photo";

const PRINCIPLE_IMAGES = [PHOTOS.strategy, PHOTOS.workshop, PHOTOS.mentor];
const ABOUT_MOSAIC = [
  { src: PHOTOS.team, label: "Collaborative consulting" },
  { src: PHOTOS.workshop, label: "Corporate workshops" },
  { src: PHOTOS.studentsLaptop, label: "Campus programs" },
  { src: PHOTOS.lectureHall, label: "Expert sessions" },
  { src: PHOTOS.chip, label: "Emerging technology" },
];
import { MEDIA, fadeInUp, staggerContainer, viewportOnce, HERO_IMAGES } from "@/constants";

const MILESTONES = [
  { id: 'mile-ai', title: "Expert AI Sessions", desc: "125+ engagements across 50+ cities, impacting decision-makers and professionals" },
  { id: 'mile-sales', title: "Sales Training Excellence", desc: "Trained 400+ sales representatives through Explore Beyond Your Boundaries\u2122" },
  { id: 'mile-catalyst', title: "AI Catalyst\u2122 Rollout", desc: "Nationwide course empowering scientists and research specialists with AI knowledge" },
  { id: 'mile-enterprise', title: "Enterprise Partnerships", desc: "Supporting growth across 6 large enterprises through strategic consulting" },
  { id: 'mile-msme', title: "MSME Transformation", desc: "Enabling 7+ mid and small enterprises through tailored digital solutions" },
  { id: 'mile-cert', title: "Global Certification", desc: "ISO/IEC 27001:2022 certified, demonstrating commitment to security excellence" },
];

const GUIDING_PRINCIPLES = [
  {
    id: 'gp-excellence',
    icon: Target,
    title: "Achieving Excellence",
    desc: "We equip individuals to excel by combining strategic foresight, meticulous planning, and hands-on expertise. Our goal is to nurture leaders who drive impact with confidence and distinction."
  },
  {
    id: 'gp-change',
    icon: Zap,
    title: "Catalyst for Change",
    desc: "Leveraging cutting-edge technology, industry-leading SMEs, and innovative thinking, we transform challenges into opportunities. Our approach unlocks new possibilities and empowers businesses to thrive."
  },
  {
    id: 'gp-growth',
    icon: TrendingUp,
    title: "Cultivating Growth Mindset",
    desc: "We believe sustainable success requires adaptability. Our solutions inspire a culture of continuous learning and development, turning obstacles into stepping stones for growth."
  }
];

export default function AboutPage() {
  return (
    <div className="pt-24">
      <SEO 
        title="About Us"
        description="Learn about Qbic Catalyst Partners - our mission, vision, expert background, and guiding principles. ISO/IEC 27001:2022 certified consulting firm empowering organizations worldwide."
        keywords="about Qbic Catalyst, consulting firm, mission vision, expert consultants, ISO certified, business transformation"
        path="/about"
      />
      {/* Hero */}
      <section className="section page-hero" style={{ "--hero-img": `url(${HERO_IMAGES.about})` }}>
        <div className="container-custom">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="max-w-4xl"
          >
            <motion.p variants={fadeInUp} className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">
              About Us
            </motion.p>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl font-bold mb-6">
              Empowering Organizations Through{' '}
              <span className="text-gradient">Innovation & Expertise</span>
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-xl text-[#A0ABC0]">
              We are a global consulting and training firm dedicated to transforming businesses and individuals for the future.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section theme-light tint-ivory">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={viewportOnce}
              className="p-8 bg-[#0A1628] rounded-lg border border-[#2A3648]"
            >
              <div className="icon-container mb-6">
                <Target className="text-[#C9A962]" size={28} />
              </div>
              <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
              <p className="text-[#A0ABC0]">
                To empower individuals and organizations by providing strategic guidance, precise execution, and expert support that unlocks their full potential. We strive to demystify artificial intelligence, foster innovation, and nurture leadership capabilities across the globe.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={viewportOnce}
              className="p-8 bg-[#0A1628] rounded-lg border border-[#2A3648]"
            >
              <div className="icon-container mb-6">
                <Lightbulb className="text-[#C9A962]" size={28} />
              </div>
              <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
              <p className="text-[#A0ABC0]">
                To be the foremost catalyst for transformation, bridging the gap between knowledge and opportunity on a global scale. We envision a world where every organization and individual has access to the ideas, experts, and technologies needed to thrive.
              </p>
            </motion.div>
          </div>
          <div className="mt-12">
            <PhotoMosaic items={ABOUT_MOSAIC} testId="about-mosaic" />
          </div>
        </div>
      </section>

      {/* Expert Background */}
      <section className="section bg-[#0A1628]">
        <div className="container-custom">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={viewportOnce}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <p className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">
                Leadership
              </p>
              <h2 className="text-3xl md:text-4xl font-bold">
                From Idea to Impact
              </h2>
            </motion.div>
            <motion.div variants={fadeInUp} className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="flex justify-center lg:justify-end">
                <img 
                  src={MEDIA.expertLeader} 
                  alt="Qbic Catalyst Expert"
                  className="rounded-lg shadow-2xl max-w-sm w-full object-cover"
                />
              </div>
              <div className="bg-[#121E30] rounded-lg p-8 border border-[#2A3648]">
                <p className="text-[#A0ABC0] mb-4">
                  Qbic Catalyst Partners was founded in 2023 with a vision to democratize access to world-class consulting and training services. Our experts bring decades of experience spanning enterprise technology, strategic consulting, and workforce development across Fortune 500 companies and emerging markets.
                </p>
                <p className="text-[#A0ABC0] mb-4">
                  With a deep understanding of the challenges organizations face in today's rapidly evolving landscape, the founding team assembled a network of industry experts, technologists, and thought leaders committed to driving meaningful transformation.
                </p>
                <p className="text-[#A0ABC0]">
                  This expertise is channeled into every engagement, ensuring our clients receive actionable strategies backed by real-world experience and cutting-edge insights.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <PhotoBand
        src={PHOTOS.conference}
        eyebrow="Our Reach"
        title="125+ expert sessions across 50+ cities"
        text="From boardrooms to campuses, we bring ideas, experts and technologies to the people who put them to work."
        testId="about-band"
      />

      {/* Impact & Milestones */}
      <section className="section theme-light tint-sky">
        <div className="container-custom">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={viewportOnce}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.p variants={fadeInUp} className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">
              Our Journey
            </motion.p>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold">
              Impact & Key Milestones
            </motion.h2>
          </motion.div>

          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={viewportOnce}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {MILESTONES.map((item) => (
              <motion.div
                key={item.id}
                variants={fadeInUp}
                className="p-6 bg-[#0A1628] rounded-lg border border-[#2A3648] card-hover flex flex-col h-full"
              >
                <CheckCircle className="text-[#C9A962] mb-4" size={24} />
                <h4 className="font-bold mb-2">{item.title}</h4>
                <p className="text-[#A0ABC0] text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Guiding Principles */}
      <section className="section bg-[#0A1628]">
        <div className="container-custom">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={viewportOnce}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.p variants={fadeInUp} className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">
              Our Values
            </motion.p>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold">
              Guiding Principles
            </motion.h2>
          </motion.div>

          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={viewportOnce}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {GUIDING_PRINCIPLES.map((item, i) => (
              <motion.div
                key={item.id}
                variants={fadeInUp}
                className="group overflow-hidden p-8 bg-[#121E30] rounded-lg border border-[#2A3648] card-hover flex flex-col h-full"
              >
                <CardImage src={PRINCIPLE_IMAGES[i]} alt={item.title} />
                <div className="icon-container mb-6">
                  <item.icon className="text-[#C9A962]" size={28} />
                </div>
                <h3 className="text-xl font-bold mb-4">{item.title}</h3>
                <p className="text-[#A0ABC0]">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
